import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, Printer, Bluetooth, Ban } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import PosShell from "@/components/pos/PosShell";
import { formatINR, itemAmount, posApi } from "@/lib/pos";
import { bluetoothSupported, printViaBluetooth, printViaBrowser } from "@/lib/escpos";
import { useAuth } from "@/context/AuthContext";

const PERIODS = [
  { id: "daily", label: "Daily", api: "day" },
  { id: "monthly", label: "Monthly", api: "month" },
  { id: "yearly", label: "Yearly", api: "year" },
];

export default function PosHistory() {
  const { token } = useAuth();
  const [period, setPeriod] = useState("daily");
  const [groups, setGroups] = useState([]);
  const [openKey, setOpenKey] = useState(null);
  const [details, setDetails] = useState({});
  const [today, setToday] = useState(null);
  const [voidTarget, setVoidTarget] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    const [g, s] = await Promise.all([
      posApi.groups(token, period),
      posApi.todaySummary(token),
    ]);
    setGroups(g);
    setToday(s.total_sales);
    setOpenKey(null);
  }, [token, period]);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = async (key) => {
    if (openKey === key) {
      setOpenKey(null);
      return;
    }
    setOpenKey(key);
    if (!details[key]) {
      const apiPeriod = PERIODS.find((p) => p.id === period).api;
      const rows = await posApi.listTransactions(token, { period: apiPeriod, key });
      setDetails((d) => ({ ...d, [key]: rows }));
    }
  };

  const confirmVoid = async () => {
    const t = voidTarget;
    setVoidTarget(null);
    try {
      await posApi.voidTransaction(token, t.id);
      toast.success(`${t.receipt_no} voided`);
      const apiPeriod = PERIODS.find((p) => p.id === period).api;
      const [rows, g, s] = await Promise.all([
        posApi.listTransactions(token, { period: apiPeriod, key: openKey }),
        posApi.groups(token, period),
        posApi.todaySummary(token),
      ]);
      setDetails((d) => ({ ...d, [openKey]: rows }));
      setGroups(g);
      setToday(s.total_sales);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not void this bill");
    }
  };

  const bt = async (txn) => {
    try {
      await printViaBluetooth(txn);
      toast.success("Sent to thermal printer");
    } catch (e) {
      toast.error(e?.message || "Bluetooth print failed");
    }
  };

  return (
    <PosShell todayTotal={today}>
      <div
        data-testid="pos-history"
        className="mx-auto max-w-5xl px-4 sm:px-6 py-5 space-y-4"
      >
        <h1 className="font-black text-2xl text-slate-900">Transaction history</h1>

        <div className="grid grid-cols-3 gap-2 p-1 rounded-[4px] bg-white border-2 border-slate-200">
          {PERIODS.map((p) => (
            <button
              key={p.id}
              type="button"
              data-testid={`history-period-${p.id}`}
              onClick={() => setPeriod(p.id)}
              className={`h-12 rounded-[4px] font-black text-sm uppercase tracking-[0.1em] transition-colors ${
                period === p.id
                  ? "bg-slate-900 text-white"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {groups.length === 0 && (
          <div className="rounded-[4px] bg-white border-2 border-slate-200 p-10 text-center text-slate-400 font-semibold">
            No transactions recorded yet
          </div>
        )}

        <div className="space-y-3">
          {groups.map((g) => (
            <div
              key={g.key}
              data-testid={`history-group-${g.key}`}
              className="rounded-[4px] bg-white border-2 border-slate-200 overflow-hidden"
            >
              <button
                type="button"
                data-testid={`history-group-toggle-${g.key}`}
                onClick={() => toggle(g.key)}
                className="w-full px-4 py-4 flex items-center justify-between gap-3 text-left"
              >
                <div>
                  <div className="font-black text-lg text-slate-900">{g.key}</div>
                  <div className="text-xs font-bold uppercase tracking-[0.12em] text-slate-500 mt-1">
                    {g.count} txn · cash {formatINR(g.cash_received)} · change{" "}
                    {formatINR(g.change_given)}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-black text-2xl tabular-nums text-slate-900">
                    {formatINR(g.total_sales)}
                  </span>
                  <ChevronDown
                    size={20}
                    className={`text-slate-400 transition-transform ${
                      openKey === g.key ? "rotate-180" : ""
                    }`}
                  />
                </div>
              </button>

              {openKey === g.key && (
                <div className="border-t-2 border-slate-100 divide-y divide-slate-100">
                  {(details[g.key] || []).map((t) => (
                    <div key={t.id} data-testid={`txn-row-${t.receipt_no}`} className={`p-4 ${t.voided ? "bg-slate-50" : ""}`}>
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <div className="font-black text-slate-900 flex items-center gap-2">
                            <span className={t.voided ? "line-through text-slate-400" : ""}>
                              {t.receipt_no}
                            </span>
                            {t.voided && (
                              <span
                                data-testid={`voided-badge-${t.receipt_no}`}
                                className="px-2 py-0.5 rounded-[4px] bg-[#fee2e2] text-[#b91c1c] text-[10px] font-black uppercase tracking-[0.14em]"
                              >
                                Voided
                              </span>
                            )}
                          </div>
                          <div className="text-xs font-bold text-slate-500 mt-0.5">
                            {t.local_date} · {t.local_time}
                          </div>
                        </div>
                        <div className="text-right">
                          <div
                            className={`font-black text-xl tabular-nums ${
                              t.voided ? "line-through text-slate-400" : "text-slate-900"
                            }`}
                          >
                            {formatINR(t.total)}
                          </div>
                          <div className="text-[11px] font-bold text-slate-500 tabular-nums">
                            cash {formatINR(t.cash_received)} · change{" "}
                            {formatINR(t.change_given)}
                          </div>
                        </div>
                      </div>

                      <div className="mt-3 rounded-[4px] bg-slate-50 p-3 space-y-1">
                        {(t.items || []).map((it, i) => (
                          <div
                            key={i}
                            className="flex justify-between text-sm font-semibold text-slate-700"
                          >
                            <span>
                              {it.label || `Item ${i + 1}`}
                              {it.qty > 1 ? ` × ${it.qty}` : ""}
                            </span>
                            <span className="tabular-nums">{formatINR(itemAmount(it))}</span>
                          </div>
                        ))}
                      </div>

                      <div className="mt-3 flex gap-2">
                        <button
                          type="button"
                          data-testid={`reprint-browser-${t.receipt_no}`}
                          onClick={() => printViaBrowser(t)}
                          className="h-10 px-3 rounded-[4px] bg-white border-2 border-slate-300 hover:border-slate-900 font-bold text-slate-800 text-xs flex items-center gap-2"
                        >
                          <Printer size={14} /> Print
                        </button>
                        <button
                          type="button"
                          data-testid={`reprint-bluetooth-${t.receipt_no}`}
                          onClick={() => bt(t)}
                          disabled={!bluetoothSupported()}
                          className="h-10 px-3 rounded-[4px] bg-[#0e6ea6] hover:bg-[#0b5c8b] disabled:opacity-40 text-white font-bold text-xs flex items-center gap-2"
                        >
                          <Bluetooth size={14} /> Thermal
                        </button>
                        {!t.voided && (
                          <button
                            type="button"
                            data-testid={`void-${t.receipt_no}`}
                            onClick={() => setVoidTarget(t)}
                            className="h-10 px-3 rounded-[4px] bg-white border-2 border-[#fecaca] text-[#b91c1c] hover:bg-[#fee2e2] font-bold text-xs flex items-center gap-2 ml-auto"
                          >
                            <Ban size={14} /> Void
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  {(details[g.key] || []).length === 0 && (
                    <div className="p-4 text-sm font-semibold text-slate-400">
                      Loading…
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <AlertDialog open={!!voidTarget} onOpenChange={(v) => !v && setVoidTarget(null)}>
        <AlertDialogContent data-testid="void-confirm-dialog" className="rounded-[4px] font-pos">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-black text-slate-900">
              Void {voidTarget?.receipt_no}?
            </AlertDialogTitle>
            <AlertDialogDescription className="font-semibold text-slate-600">
              This bill of {formatINR(voidTarget?.total || 0)} will be struck through
              and removed from all sales totals. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="void-cancel" className="rounded-[4px] font-bold">
              Keep bill
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="void-confirm"
              onClick={confirmVoid}
              className="rounded-[4px] bg-[#b91c1c] hover:bg-[#991b1b] font-bold"
            >
              Void bill
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PosShell>
  );
}
